import { useState, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast, ToastMessage } from "primereact/toast";
import RawMaterialService from "../../../services/RawMaterials/RawMaterialService";

const RMCategorySearch: React.FC<{ onSearch: (keyword: string) => void; onAdded: () => void }> = ({ onSearch, onAdded }) => {
  const [selectedKeyword, setSelectedKeyword] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryNameError, setCategoryNameError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const toast = useRef<Toast>(null);

  const showMessage = (label: string, message: string, severity: ToastMessage["severity"]) => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  const handleSearch = () => onSearch(selectedKeyword);
  const resetSearch = () => {
    setSelectedKeyword("");
    onSearch("");
  };

  // ✅ Validation helpers
  const allowOnlyLetters = (value: string) => /^[A-Za-z\s]*$/.test(value);
  const allowDescription = (value: string) => /^[A-Za-z\s,\.]*$/.test(value);

  const handleSave = async () => {
    let valid = true;

    if (!categoryName.trim()) {
      setCategoryNameError("Category name is required.");
      valid = false;
    }
    if (!description.trim()) {
      setDescriptionError("Description is required.");
      valid = false;
    }

    if (categoryName.trim() && !allowOnlyLetters(categoryName)) {
      setCategoryNameError("Category name can only contain letters and spaces.");
      valid = false;
    }

    if (description.trim() && !allowDescription(description)) {
      setDescriptionError("Description can only contain letters, spaces, commas, and full stops.");
      valid = false;
    }

    if (!valid) return;

    try {
      const response = await RawMaterialService.addRawMaterialCategory({
        CategoryName: categoryName.trim(),
        Description: description.trim(),
      });

      if (response.isSuccess) {
        showMessage("Add Category", "Category added successfully", "success");
        setShowAddModal(false);
        setCategoryName("");
        setDescription("");
        onAdded();
      } else {
        showMessage("Add Category", response.message || "Failed to add category", "error");
      }
    } catch (error) {
      console.error("Error adding category:", error);
      showMessage("Add Category", "Unexpected error occurred.", "error");
    }
  };

  return (
    <div className="col-12">
      <div
        className="card border-noround p-2"
        style={{ minHeight: "3.5rem", border: "1px solid #ccc" }}
      >
        <div className="p-input-icon-left" style={{ float: "left", width: "18%" }}>
          <i className="pi pi-search" />
          <InputText
            type="search"
            value={selectedKeyword}
            onChange={(e) => setSelectedKeyword(e.target.value)}
            placeholder="Keyword Search"
            style={{ width: "100%" }}
          />
        </div>
        <div style={{ float: "left", width: "10%", marginLeft: "8px" }}>
          <Button style={{ width: "100%" }} onClick={handleSearch} severity="success">
            Filter
          </Button>
        </div>
        <div style={{ float: "left", width: "10%", marginLeft: "8px" }}>
          <Button style={{ width: "100%" }} onClick={resetSearch} severity="secondary" text raised>
            Reset
          </Button>
        </div>
        <div style={{ marginLeft: "12px", float: "right" }}>
          <Button className="bg-blue-700 text-white" onClick={() => setShowAddModal(true)}>
            Add Category
          </Button>
        </div>

        <Dialog
          visible={showAddModal}
          style={{ width: "550px", height: "400px" }}
          className="quickview w-40"
          draggable={false}
          modal
          onHide={() => setShowAddModal(false)}
        >
          <h5 className="text-center mb-3 text-white bg-green-600 py-2 px-4 rounded-md">
            ADD CATEGORY
          </h5>
          <div className="mb-3">
            <label>Category Name *</label>
            <InputText
              value={categoryName}
              onChange={(e) => {
                setCategoryName(e.target.value);
                if (e.target.value.trim() && allowOnlyLetters(e.target.value)) setCategoryNameError("");
              }}
              style={{ 
           width: "100%", 
                border: "1px solid #a0a59fff", 
                backgroundColor: "white",
                boxShadow: "0 0 0 0.2rem rgba(155, 177, 143, 0.25)"
              }}
              autoFocus
            />
            {categoryNameError && <small className="p-error">{categoryNameError}</small>}
          </div>
          <div className="mb-3">
            <label>Description *</label>
            <InputText
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (e.target.value.trim() && allowDescription(e.target.value)) setDescriptionError("");
              }}
              style={{ 
             width: "100%", 
                border: "1px solid #a0a59fff", 
                backgroundColor: "white",
                boxShadow: "0 0 0 0.2rem rgba(155, 177, 143, 0.25)"
              }}
            />
            {descriptionError && <small className="p-error">{descriptionError}</small>}
          </div>
          <div className="text-right mt-4">
            <Button className="bg-green-700 text-white" onClick={handleSave}>
              Save
            </Button>
          </div>
        </Dialog>
        <Toast ref={toast} />
      </div>
    </div>
  );
};

export default RMCategorySearch;